import type { ReactNode } from "react";
import { makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import { REFINED } from "../risikoanalyseStep/tokens";
import { VERBUND_EBENEN, type VerbundScope } from "./useVerbundScope";

/**
 * Leiste „Dieses Unternehmen / Unternehmensverbund" über einem Kontakt-Reiter: Ebenen-Pills,
 * darunter die Firmen des Verbunds als Chips (optional mit Zähler) und Platz für
 * reiter-eigene Filterzeilen (`children`). Rein präsentational — der ganze State
 * kommt aus `useVerbundScope`.
 *
 * Bewusst durchgängig REFINED-Tokens (Aubergine/Hanken Grotesk): die
 * Design-Guideline verbietet, REFINED und das ältere BRAND-System auf einer
 * Fläche zu mischen.
 */

const useStyles = makeStyles({
  bar: {
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
    ...shorthands.padding("10px", "14px"),
    backgroundColor: REFINED.aub50,
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("12px"),
    fontFamily: REFINED.fontUI,
    color: REFINED.ink,
  },
  row: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    columnGap: "8px",
    rowGap: "6px",
  },
  // `ink2` statt `ink3`: auf der Aubergine-Fläche der Leiste käme `ink3` nur auf
  // 2,6:1 — für tragende Beschriftungen und Hinweise zu wenig (AA verlangt 4,5).
  label: {
    fontSize: `${REFINED.fs.xxs}px`,
    fontWeight: 700,
    letterSpacing: "0.09em",
    textTransform: "uppercase",
    color: REFINED.ink2,
    minWidth: "56px",
  },
  seg: {
    display: "inline-flex",
    columnGap: "2px",
    ...shorthands.padding("2px"),
    backgroundColor: REFINED.white,
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("999px"),
  },
  segBtn: {
    ...shorthands.borderWidth("0"),
    ...shorthands.borderRadius("999px"),
    ...shorthands.padding("5px", "13px"),
    backgroundColor: "transparent",
    fontFamily: REFINED.fontUI,
    fontSize: `${REFINED.fs.sm}px`,
    fontWeight: 600,
    color: REFINED.ink2,
    cursor: "pointer",
    transitionProperty: "background-color, color",
    transitionDuration: "140ms",
    transitionTimingFunction: "ease",
  },
  segBtnOn: {
    backgroundColor: REFINED.aub,
    color: REFINED.white,
    fontWeight: 700,
    boxShadow: "0 1px 3px rgba(58,12,49,0.25)",
  },
  hint: { fontSize: `${REFINED.fs.xs}px`, color: REFINED.ink2 },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    ...shorthands.padding("4px", "11px"),
    backgroundColor: REFINED.white,
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("999px"),
    fontFamily: REFINED.fontUI,
    fontSize: `${REFINED.fs.xs}px`,
    fontWeight: 600,
    color: REFINED.ink2,
    cursor: "pointer",
  },
  chipOn: {
    backgroundColor: REFINED.aub100,
    ...shorthands.borderColor(REFINED.aub),
    color: REFINED.ink,
    fontWeight: 700,
  },
  chipRole: { color: REFINED.ink2, fontWeight: 600 },
  chipCount: {
    fontSize: `${REFINED.fs.xxs}px`,
    fontWeight: 700,
    color: REFINED.aub,
    backgroundColor: REFINED.aub50,
    ...shorthands.borderRadius("999px"),
    ...shorthands.padding("1px", "6px"),
  },
  chipCountOn: { backgroundColor: REFINED.white },
  // Ab CHIP_LIMIT Firmen: kompakte Mehrfachauswahl statt einer Chip-Tapete
  listWrap: {
    display: "flex",
    flexDirection: "column",
    rowGap: "4px",
    flexGrow: 1,
    minWidth: 0,
  },
  list: {
    maxHeight: "132px",
    overflowY: "auto",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))",
    columnGap: "4px",
    ...shorthands.padding("6px", "8px"),
    backgroundColor: REFINED.white,
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("10px"),
  },
  listItem: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    ...shorthands.padding("3px", "5px"),
    ...shorthands.borderRadius("6px"),
    fontSize: `${REFINED.fs.sm}px`,
    color: REFINED.ink2,
    cursor: "pointer",
    ":hover": { backgroundColor: REFINED.aub50 },
  },
  listItemOn: { color: REFINED.ink, fontWeight: 600 },
  checkbox: {
    width: "14px",
    height: "14px",
    ...shorthands.margin("0"),
    accentColor: REFINED.aub,
    flexShrink: 0,
    cursor: "pointer",
  },
  listName: {
    overflowX: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  listCount: {
    marginLeft: "auto",
    fontSize: `${REFINED.fs.xxs}px`,
    fontWeight: 700,
    color: REFINED.aub,
  },
});

export interface VerbundScopeBarProps {
  scope: VerbundScope;
  /** Zähler je Firma (z. B. Verträge, Risikoobjekte) für Chips/Liste. */
  counts?: Map<number, number>;
  /** Text rechts neben den Ebenen-Pills; ohne Angabe die Erklärung der Ebene. */
  hint?: ReactNode;
  /** Zusätzliche Filterzeilen des jeweiligen Reiters. */
  children?: ReactNode;
}

const firmLabel = (firm: { id: number; name: string | null }): string =>
  firm.name ?? `Kontakt ${firm.id}`;

export function VerbundScopeBar({ scope, counts, hint, children }: VerbundScopeBarProps) {
  const s = useStyles();
  const {
    ebene,
    setEbene,
    scopeFirmen,
    selected,
    toggleFirm,
    networkLoading,
    tooManyFirms,
    maxSelected,
    selectionCapped,
  } = scope;

  const renderFirmen = () => {
    if (ebene === "firma") return null;
    if (networkLoading)
      return (
        <div className={s.row}>
          <span className={s.label}>Firmen</span>
          <span className={s.hint}>Unternehmensverbund wird geladen …</span>
        </div>
      );
    // Einzelne Firma ohne Verbund: die Chip-Zeile hätte nichts zu wählen
    if (scopeFirmen.length <= 1) return null;

    if (tooManyFirms)
      return (
        <div className={s.row}>
          <span className={s.label}>Firmen</span>
          <div className={s.listWrap}>
            <span className={s.hint}>
              {scope.selectedIds.length} von {scopeFirmen.length} Firmen gewählt
              {/* Ohne diesen Zusatz sähe eine gedeckelte Auswahl aus wie eine
                  frei getroffene — und die Summe darüber wie die des ganzen
                  Verbunds. */}
              {selectionCapped ? ` · höchstens ${maxSelected} gleichzeitig` : ""}
            </span>
            <div className={s.list}>
              {scopeFirmen.map((f) => {
                const on = selected.has(f.id);
                return (
                  <label
                    key={f.id}
                    className={mergeClasses(s.listItem, on && s.listItemOn)}
                    title={firmLabel(f)}
                  >
                    <input
                      type="checkbox"
                      className={s.checkbox}
                      checked={on}
                      onChange={() => toggleFirm(f.id)}
                    />
                    <span className={s.listName}>{firmLabel(f)}</span>
                    {counts && <span className={s.listCount}>{counts.get(f.id) ?? 0}</span>}
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      );

    return (
      <div className={s.row}>
        <span className={s.label}>Firmen</span>
        {scopeFirmen.map((f) => {
          const on = selected.has(f.id);
          return (
            <button
              key={f.id}
              type="button"
              aria-pressed={on}
              className={mergeClasses(s.chip, on && s.chipOn)}
              onClick={() => toggleFirm(f.id)}
            >
              {firmLabel(f)}
              {f.role ? <span className={s.chipRole}>· {f.role}</span> : null}
              {counts && (
                <span className={mergeClasses(s.chipCount, on && s.chipCountOn)}>
                  {counts.get(f.id) ?? 0}
                </span>
              )}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className={s.bar}>
      <div className={s.row}>
        <span className={s.label}>Ebene</span>
        <div className={s.seg}>
          {VERBUND_EBENEN.map((e) => (
            <button
              key={e.id}
              type="button"
              aria-pressed={ebene === e.id}
              className={mergeClasses(s.segBtn, ebene === e.id && s.segBtnOn)}
              onClick={() => setEbene(e.id)}
            >
              {e.label}
            </button>
          ))}
        </div>
        <span className={s.hint}>
          {hint ?? VERBUND_EBENEN.find((e) => e.id === ebene)?.hint}
        </span>
      </div>
      {renderFirmen()}
      {children}
    </div>
  );
}

export default VerbundScopeBar;
